import { motion } from "framer-motion";
import { ArrowUpRight, Check, QrCode, ReceiptText, Store, UtensilsCrossed } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];

const SUITES = [
  {
    id: "hostpoint-pos",
    testid: "flagship-hostpoint-pos",
    icon: UtensilsCrossed,
    label: "In-House Flagship — Hospitality",
    name: "HostPoint POS",
    tagline:
      "The all-in-one, KRA eTIMS-ready POS that runs your restaurant, bar or hotel end to end.",
    features: [
      "M-Pesa STK, Pesapal & Network International card terminals",
      "Real-time KRA eTIMS fiscal receipts with QR verification",
      "QR scan-to-order menus — no app download for guests",
      "Inventory, procurement, staff shifts & multi-outlet analytics",
    ],
    mainImg: "https://hostpointpos.com/showcase/pos-menu.png",
    floatImg: "https://hostpointpos.com/showcase/pos-settlement.png",
    floatAlt: "HostPoint flexible settlement screen",
    mainAlt: "HostPoint POS fast touch ordering screen",
    link: "https://hostpointpos.com/",
    price: "From KSh 4,500/mo · 30-day free trial",
  },
  {
    id: "hostpoint-retail",
    testid: "flagship-hostpoint-retail",
    icon: Store,
    label: "In-House Flagship — Retail",
    name: "HostPoint Retail",
    tagline:
      "Retail POS, eCommerce and loyalty — built for Kenya's fashion and lifestyle retailers.",
    features: [
      "Size & colour variant matrix inventory across stores",
      "Built-in branded online store — one catalogue, two channels",
      "Loyalty points, tiers and a customer mobile app",
      "KRA eTIMS fiscal receipts, credit notes & import reconciliation",
    ],
    mainImg: "/assets/hostpoint-retail-receipt.png",
    floatImg: null,
    floatAlt: "",
    mainAlt: "HostPoint Retail KRA eTIMS fiscal receipt with verification QR",
    frame: "card",
    link: "https://hostpointretail.com/",
    price: "From KSh 3,000/mo · 30-day free trial",
  },
];

export default function Flagships() {
  return (
    <section id="flagships" className="relative py-28 lg:py-36 bg-[#F5F8FC] border-y border-[#263562]/10 overflow-hidden">
      <div
        aria-hidden="true"
        className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[400px] bg-[radial-gradient(ellipse_at_top,rgba(58,176,225,0.10),transparent_65%)]"
      />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: EASE }}
          className="mb-20 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6"
        >
          <div>
            <span className="font-mono-tech text-[11px] uppercase tracking-[0.3em] text-[#263562]/60">
              Chapter 03 — The Proof
            </span>
            <h2 className="mt-4 font-display font-bold tracking-tight leading-tight text-3xl sm:text-4xl lg:text-5xl max-w-2xl text-[#263562]">
              We don't just distribute software.{" "}
              <span className="text-[#3AB0E1]">We build it.</span>
            </h2>
          </div>
          <p className="max-w-md text-base leading-relaxed text-[#5A6B85] font-light">
            HostPoint is our own engineering — fiscalised, mobile-money native and
            supported from Nairobi. This is the stack we stake our name on.
          </p>
        </motion.div>

        <div className="flex flex-col gap-24">
          {SUITES.map((s, idx) => (
            <motion.article
              key={s.id}
              data-testid={s.testid}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9, ease: EASE }}
              className={`grid lg:grid-cols-2 gap-12 lg:gap-16 items-center ${
                idx % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""
              }`}
            >
              <div>
                <span className="inline-flex items-center gap-2 rounded-full border border-[#3AB0E1]/50 bg-[#3AB0E1]/10 px-4 py-1.5 font-mono-tech text-[10px] uppercase tracking-[0.2em] text-[#1E8FBF]">
                  <s.icon className="w-3.5 h-3.5" />
                  {s.label}
                </span>
                <h3 className="mt-6 font-display font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-[#263562]">
                  {s.name}
                </h3>
                <p className="mt-5 text-base sm:text-lg leading-relaxed text-[#3D4E6E] font-light max-w-lg">
                  {s.tagline}
                </p>
                <ul className="mt-8 space-y-3.5">
                  {s.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm sm:text-base text-[#5A6B85]">
                      <span className="mt-0.5 w-5 h-5 rounded-full bg-[#3AB0E1]/15 border border-[#3AB0E1]/50 flex items-center justify-center shrink-0">
                        <Check className="w-3 h-3 text-[#1E8FBF]" />
                      </span>
                      {f}
                    </li>
                  ))}
                </ul>
                <div className="mt-10 flex flex-wrap items-center gap-5">
                  <a
                    data-testid={`${s.testid}-link`}
                    href={s.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex items-center gap-2 bg-[#263562] hover:bg-[#3AB0E1] text-white font-semibold px-6 py-3.5 rounded-full transition-all duration-300 hover:shadow-[0_10px_35px_rgba(58,176,225,0.4)]"
                  >
                    Visit {s.name}
                    <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
                  </a>
                  <span className="font-mono-tech text-[11px] uppercase tracking-[0.15em] text-[#949CA1] flex items-center gap-2">
                    <ReceiptText className="w-3.5 h-3.5 text-[#3AB0E1]" />
                    {s.price}
                  </span>
                </div>
              </div>

              <div className="relative">
                <div className="tracing-beam rounded-2xl p-[1.5px]">
                  {s.frame === "card" ? (
                    <div className="rounded-2xl bg-white bg-[radial-gradient(circle_at_50%_20%,rgba(58,176,225,0.10),transparent_65%)] px-10 py-12 flex items-center justify-center min-h-[420px]">
                      <motion.img
                        src={s.mainImg}
                        alt={s.mainAlt}
                        loading="lazy"
                        className="w-full max-w-[300px] h-auto block rounded-lg shadow-[0_30px_60px_-15px_rgba(38,53,98,0.3)]"
                        whileHover={{ scale: 1.03, rotate: -1 }}
                        transition={{ duration: 0.6, ease: EASE }}
                      />
                    </div>
                  ) : (
                    <div className="rounded-2xl overflow-hidden bg-white">
                      <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-[#263562]/10">
                        <span className="w-2.5 h-2.5 rounded-full bg-[#C7D2E4]" />
                        <span className="w-2.5 h-2.5 rounded-full bg-[#C7D2E4]" />
                        <span className="w-2.5 h-2.5 rounded-full bg-[#3AB0E1]/70" />
                        <span className="ml-3 font-mono-tech text-[10px] text-[#949CA1] tracking-wider truncate">
                          {s.link.replace("https://", "")}
                        </span>
                      </div>
                      <motion.img
                        src={s.mainImg}
                        alt={s.mainAlt}
                        loading="lazy"
                        className="w-full h-auto block"
                        whileHover={{ scale: 1.02 }}
                        transition={{ duration: 0.6, ease: EASE }}
                      />
                    </div>
                  )}
                </div>
                {s.floatImg && (
                  <motion.div
                    initial={{ opacity: 0, y: 30, rotate: 3 }}
                    whileInView={{ opacity: 1, y: 0, rotate: 2 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: 0.3, ease: EASE }}
                    className="absolute -bottom-10 -right-4 sm:-right-8 w-40 sm:w-52 rounded-lg overflow-hidden border border-[#3AB0E1]/40 shadow-[0_25px_50px_-12px_rgba(38,53,98,0.3)] bg-white"
                  >
                    <img src={s.floatImg} alt={s.floatAlt} loading="lazy" className="w-full h-auto block" />
                    <span className="flex items-center gap-1.5 px-3 py-2 font-mono-tech text-[9px] uppercase tracking-[0.2em] text-[#263562]">
                      <QrCode className="w-3 h-3 text-[#3AB0E1]" />
                      Split · M-Pesa · Card
                    </span>
                  </motion.div>
                )}
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
